import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

function safeDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

export default function PendingShipmentsDetail() {
  const nav = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [sort, setSort] = useState({
    field: "ShipByDate",
    direction: "asc",
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/pending-shipments/detail",
          {
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
          },
          { timeout: 120000 }
        );

        const data = response?.data ?? response;

        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setSummary(data?.summary || null);
          setMeta(data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setSummary(null);
          setMeta(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Pending Shipments details could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, sort]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...searchedRows];

    if (!sort.field) return copy;

    copy.sort((a, b) => {
      const result = String(a?.[sort.field] ?? "").localeCompare(
        String(b?.[sort.field] ?? ""),
        undefined,
        { numeric: true }
      );

      return sort.direction === "desc" ? -result : result;
    });

    return copy;
  }, [searchedRows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changeSort = (field) => {
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const exportCsv = () => {
    if (!sortedRows.length) return;

    const columns = [
      "PoID",
      "PoNo",
      "PoDate",
      "ShipByDate",
      "CustomerName",
      "DestinationCountry",
      "ProcessorName",
      "ShipmentState",
    ];

    const csv = [
      columns.map(csvEscape).join(","),
      ...sortedRows.map((row) =>
        columns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "pending-shipments.csv";

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns = [
    ["PoNo", "PO No"],
    ["PoDate", "PO Date"],
    ["ShipByDate", "Ship By Date"],
    ["CustomerName", "Customer / Consignee"],
    ["DestinationCountry", "Country"],
    ["ProcessorName", "Processor / Plant"],
    ["ShipmentState", "Status"],
  ];

  return (
    <div className="erp-home-shell ceo-analytics-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content ceo-analytics-content">
        <div className="ceo-erp-content space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => nav("/ceo-command-center?tab=commercial")}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3"
              >
                <ArrowLeft size={16} />
                Back to Commercial
              </button>

              <h1 className="text-2xl font-semibold text-slate-900">
                Pending Shipments
              </h1>

              <p className="text-sm text-slate-500 mt-1 max-w-3xl">
                ERP Pending Shipment POs where Ship By Date falls inside the selected period
                and Customs Invoice Stock Out Date is blank or unavailable.
              </p>
            </div>

            <button
              type="button"
              onClick={exportCsv}
              disabled={!sortedRows.length}
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium bg-white disabled:opacity-50"
              style={{ borderColor: "var(--c-border)" }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "var(--c-border)" }}>
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Pending Shipments
                </div>
                <div className="text-3xl font-semibold text-slate-900 mt-1">
                  {Number(summary?.pending_shipments || 0).toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Distinct PO count
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4">
              <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "var(--c-border)" }}>
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Selected Period
                </div>
                <div className="text-base font-semibold text-slate-900 mt-1">
                  {fromDate || "—"} to {toDate || "—"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Date basis: ScrmPoData.ShipByDate
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4">
              <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "var(--c-border)" }}>
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Reconciliation
                </div>
                <div className="text-base font-semibold text-emerald-700 mt-1">
                  {meta?.reconciliation?.pass ? "PASS" : "Pending"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {meta?.reconciliation?.equation || "—"}
                </div>
              </div>
            </div>
          </div>

          <div
            className="bg-white border rounded-2xl p-4"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative min-w-[260px] flex-1 max-w-lg">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search PO, customer, country, processor..."
                  className="w-full rounded-xl border pl-9 pr-3 py-2 text-sm bg-white"
                  style={{ borderColor: "var(--c-border)" }}
                />
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-xl border px-3 py-2 bg-white"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option value={size} key={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div
            className="bg-white border rounded-2xl overflow-hidden"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {columns.map(([field, label]) => (
                      <th
                        key={field}
                        className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap"
                      >
                        <button
                          type="button"
                          onClick={() => changeSort(field)}
                          className="inline-flex items-center gap-1"
                        >
                          {label}
                          {sort.field === field ? (
                            sort.direction === "asc" ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )
                          ) : null}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-10 text-center text-slate-500"
                      >
                        Loading Pending Shipments...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((row) => (
                      <tr
                        key={row.PoID}
                        className="border-t"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                          {row.PoNo || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {safeDate(row.PoDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {safeDate(row.ShipByDate)}
                        </td>
                        <td className="px-4 py-3 min-w-[240px]">
                          {row.CustomerName || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.DestinationCountry || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.ProcessorName || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                            Pending
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-10 text-center text-slate-500"
                      >
                        No pending shipment POs found for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div
              className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm"
              style={{ borderColor: "var(--c-border)" }}
            >
              <div className="text-slate-500">
                Showing {sortedRows.length ? (currentPage - 1) * pageSize + 1 : 0}
                {" – "}
                {Math.min(currentPage * pageSize, sortedRows.length)}
                {" of "}
                {sortedRows.length.toLocaleString()}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  Previous
                </button>

                <span className="text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div
            className="bg-white border rounded-2xl p-4 text-xs text-slate-500"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div>
              <span className="font-semibold text-slate-700">ERP rule:</span>{" "}
              {meta?.definition || "Ship By Date in period and Stock Out Date blank/null."}
            </div>
            <div className="mt-1">
              <span className="font-semibold text-slate-700">Source:</span>{" "}
              {meta?.source?.erp_screen_logic ||
                "v3/ColdStore/Ajax/ajax-dashboard-insights.php::getShipmentPendingIDs()"}
            </div>
            <div className="mt-1">
              <span className="font-semibold text-slate-700">Grain:</span>{" "}
              {meta?.source?.grain || "Distinct ScrmPoData.PoID"}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
